import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { apiService, formatApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { StatusPill, StatusFilter, HistoryList, fmtDate } from './AdminModeration';

const STATUSES = ['open', 'reviewing', 'resolved', 'dismissed', 'all'];

export const ReportDetail = ({ report, onUpdated }) => {
  const [status, setStatus] = useState(report.status);
  const [note, setNote] = useState(report.resolution_note || '');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const save = async () => {
    setSaving(true);
    try {
      await apiService.admin.updateReport(report.id, { status, resolution_note: note || null, action_on_user: 'none' });
      toast({ title: 'Report updated' });
      onUpdated();
    } catch (e) {
      toast({ title: 'Update failed', description: formatApiError(e), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 border-t pt-4 space-y-4 text-sm" data-testid={`report-detail-${report.id}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><p className="text-gray-500">Reporter</p><p className="font-medium">{report.reporter?.name || 'Deleted user'} <span className="text-gray-400">{report.reporter?.email}</span></p></div>
        <div><p className="text-gray-500">Reported member</p><p className="font-medium">{report.reported_user?.name || 'Deleted user'} <span className="text-gray-400">{report.reported_user?.email}</span>{report.reported_user?.status && report.reported_user.status !== 'active' && <span className="ml-2 text-red-600">({report.reported_user.status})</span>}</p></div>
      </div>
      <div><p className="text-gray-500">Original report ({report.category})</p><p className="whitespace-pre-wrap bg-gray-50 rounded p-2" data-testid="report-description">{report.description || <span className="text-gray-400">No description</span>}</p></div>
      {report.additional_notes?.length > 0 && (
        <div data-testid="report-additional-notes"><p className="text-gray-500">Follow-ups from the same reporter ({report.additional_notes.length})</p>
          <ul className="space-y-1">{report.additional_notes.map((n, i) => <li key={i} className="bg-gray-50 rounded p-2"><span className="text-gray-400 text-xs">{fmtDate(n.created_at)} · {n.category}</span><br />{n.description}</li>)}</ul></div>
      )}
      <div><p className="text-gray-500 mb-1">History</p><HistoryList items={report.history} /></div>
      <div className="grid sm:grid-cols-[180px_1fr] gap-3 items-start">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-md px-3 py-2 bg-white" data-testid="report-status-select">
          {['open', 'reviewing', 'resolved', 'dismissed'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Resolution note (visible to admins only)" rows={2} data-testid="report-note-input" />
      </div>
      <Button onClick={save} disabled={saving} size="sm" data-testid="report-save-btn">{saving ? 'Saving…' : 'Save status & note'}</Button>
    </div>
  );
};

const AdminReports = () => {
  const [filter, setFilter] = useState('open');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = () => apiService.admin.getReports({ status_filter: filter, limit: 100 }).then((r) => { setData(r.data.data); setError(''); }).catch((e) => setError(formatApiError(e)));
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-4" data-testid="admin-reports">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <StatusFilter value={filter} onChange={setFilter} options={STATUSES} testId="reports-filter" />
        <div className="flex items-center gap-3 text-sm text-gray-600">{data && <span data-testid="reports-total">{data.total} report{data.total === 1 ? '' : 's'}</span>}<Button size="sm" variant="outline" onClick={load} data-testid="reports-refresh"><RefreshCw className="w-4 h-4" /></Button></div>
      </div>
      {error && <p className="text-red-600" data-testid="reports-error">{error}</p>}
      {data && data.reports.length === 0 && <Card className="p-6 text-center text-gray-500" data-testid="reports-empty">No {filter === 'all' ? '' : filter} reports.</Card>}
      {data?.reports.map((r) => (
        <Card key={r.id} className="p-4" data-testid={`report-row-${r.id}`}>
          <button type="button" className="w-full text-left flex flex-wrap items-center gap-3" onClick={() => setOpenId(openId === r.id ? null : r.id)} data-testid={`report-toggle-${r.id}`}>
            <StatusPill status={r.status} />
            <span className="font-semibold capitalize">{r.category}</span>
            <span className="text-gray-600">{r.reporter?.name || 'Deleted user'} → {r.reported_user?.name || 'Deleted user'}</span>
            {r.duplicate_count > 0 && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full" data-testid="report-duplicate-badge">+{r.duplicate_count} follow-up{r.duplicate_count === 1 ? '' : 's'}</span>}
            {r.prior_reports_against_user > 1 && <span className="text-xs text-red-600">{r.prior_reports_against_user} reports against this member</span>}
            <span className="ml-auto text-xs text-gray-400">{fmtDate(r.created_at)}</span>
          </button>
          {openId === r.id && <ReportDetail report={r} onUpdated={load} />}
        </Card>
      ))}
    </div>
  );
};

export default AdminReports;
